/**
 * OCR Quota UI Helper
 *
 * The REAL rate limit is enforced server-side (api/ocr.js via Upstash Redis),
 * tracked per buyerId AND IP address.
 *
 * This module handles:
 * - Caching the last known quota state in localStorage (keyed per buyerId)
 *   so the UI can show remaining count without a round-trip
 * - Updating cached state from server responses
 * - Time formatting utilities
 */

const STORAGE_PREFIX = 'cw_ocr_quota_'; // keyed as cw_ocr_quota_<buyerId>
const DAILY_LIMIT = 10;
const WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get storage key specific to this user.
 * @param {string} buyerId
 */
function storageKey(buyerId) {
  return `${STORAGE_PREFIX}${buyerId || 'guest'}`;
}

/**
 * Read cached quota for this user from localStorage.
 */
function readCached(buyerId) {
  try {
    const raw = localStorage.getItem(storageKey(buyerId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Write quota state to localStorage cache for this user.
 */
function writeCached(buyerId, data) {
  try {
    localStorage.setItem(storageKey(buyerId), JSON.stringify(data));
  } catch {
    // Fail silently if localStorage unavailable
  }
}

/**
 * Update the local quota cache from the server's rate-limit response.
 * Call this after receiving any response from /api/ocr.
 *
 * @param {string} buyerId - The user's buyer ID
 * @param {{ remaining: number, limit: number, resetAt: number }} serverData
 */
export function syncQuotaFromServer(buyerId, { remaining, limit, resetAt }) {
  writeCached(buyerId, {
    remaining: typeof remaining === 'number' ? remaining : DAILY_LIMIT,
    limit: typeof limit === 'number' ? limit : DAILY_LIMIT,
    resetAt: typeof resetAt === 'number' ? resetAt : Date.now() + WINDOW_MS,
    syncedAt: Date.now(),
  });
}

/**
 * Optimistically decrement the cached count by 1 (call after a successful scan).
 * The server is the source of truth, but this makes the UI update instantly.
 *
 * @param {string} buyerId
 */
export function decrementLocalQuota(buyerId) {
  // If no cache exists yet, create a fresh one from the default limit
  const cached = readCached(buyerId) || {
    remaining: DAILY_LIMIT,
    limit: DAILY_LIMIT,
    resetAt: Date.now() + WINDOW_MS,
    syncedAt: Date.now(),
  };
  writeCached(buyerId, {
    ...cached,
    remaining: Math.max(0, cached.remaining - 1),
  });
}

/**
 * Get the current quota state for a user.
 * Returns server-synced data if available and fresh (< 24hrs), otherwise a safe default.
 *
 * @param {string} buyerId
 * @returns {{ allowed: boolean, remaining: number, limit: number, resetAt: number, resetInMs: number }}
 */
export function getQuotaStatus(buyerId) {
  const cached = readCached(buyerId);
  const now = Date.now();

  if (cached && cached.resetAt && cached.resetAt > now) {
    const resetInMs = cached.resetAt - now;
    return {
      allowed: cached.remaining > 0,
      remaining: cached.remaining,
      limit: cached.limit || DAILY_LIMIT,
      resetAt: cached.resetAt,
      resetInMs,
    };
  }

  // No valid cache — assume full quota (server will enforce the real limit)
  return {
    allowed: true,
    remaining: DAILY_LIMIT,
    limit: DAILY_LIMIT,
    resetAt: now + WINDOW_MS,
    resetInMs: WINDOW_MS,
  };
}

/**
 * Format milliseconds as "Xh Ym" or "Ym" or "less than a minute".
 * @param {number} ms
 * @returns {string}
 */
export function formatResetTime(ms) {
  if (!ms || ms <= 0) return 'shortly';
  const totalSecs = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSecs / 3600);
  const minutes = Math.floor((totalSecs % 3600) / 60);
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return 'less than a minute';
}

/**
 * Format epoch ms as a local time string (e.g. "09:30 AM").
 * @param {number} epochMs
 * @returns {string}
 */
export function formatResetClock(epochMs) {
  if (!epochMs) return '--:--';
  return new Date(epochMs).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Fetch the latest quota status from the server without consuming a token.
 * Useful for syncing multiple devices on initial load.
 *
 * @param {string} buyerId
 * @returns {Promise<Object|null>}
 */
export async function fetchQuotaFromServer(buyerId) {
  if (!buyerId) return null;
  try {
    const res = await fetch(`/api/ocr-status?buyerId=${encodeURIComponent(buyerId)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data && typeof data.remaining === 'number') {
      syncQuotaFromServer(buyerId, data);
      return getQuotaStatus(buyerId);
    }
    return null;
  } catch (err) {
    console.error('Failed to sync quota from server:', err);
    return null;
  }
}
